import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import navigateToURL from 'lib/navigateToURL';
import notifications from 'notifications';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  composeProcedureEventMetadataFromFlowchart,
  composeProcedureEventMetadata,
  PROCEDURE_DUPLICATE_ENDED_EVENT,
  PROCEDURE_DUPLICATE_STARTED_EVENT,
} from 'performance/processProceduresEvents';
import {duplicateFlowchart} from 'procedures/design/flowchartManagementUtils';
import * as ProcedureDAO from 'procedures/network/ProcedureDAO';
import {procedurePageUrl} from 'procedures/network/procedureUrls';
import serializeProcedureFlowchartForNetwork from 'procedures/network/serializeProcedureFlowchartForNetwork';
import {trackDuplicatedProcedureEvent} from 'procedures/procedureAnalyticsEventsUtil';
import {ProcedureFlowchart, ProcedureWithMetadata} from 'procedures/ProcedureDomainTypes';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import bufferForUX from 'util/bufferForUX';
import {withLoadingScreen} from 'util/Promises';

function getClonedFlowchart(flowchart: ProcedureFlowchart): {
  newFlowchart: ProcedureFlowchart;
  cloneStepCidToDocumentVersionIdMap: Record<string, string>;
} {
  const {newFlowchart, cloneStepCidToDocumentVersionIdMap} = duplicateFlowchart(flowchart);
  return {
    newFlowchart: {
      edges: [...newFlowchart.edges],
      nodes: [...newFlowchart.nodes],
    },
    cloneStepCidToDocumentVersionIdMap,
  };
}

interface CreateDuplicateProcedureParams {
  name: string;
  procedureIdToDuplicate: string;
  templateCollectionId: string;
  procedureType: ProcedureType;
  flowchart: ProcedureFlowchart;
  cloneStepCidToDocumentVersionIdMap: Record<string, string>;
}

async function createDuplicateProcedure({
  name,
  procedureIdToDuplicate,
  templateCollectionId,
  procedureType,
  flowchart,
  cloneStepCidToDocumentVersionIdMap,
}: CreateDuplicateProcedureParams): Promise<{id: string}> {
  const {
    createdIds: [createdId],
  } = await ProcedureDAO.createProcedure({
    name,
    templateCollectionId,
    procedureType,
    flowchart: serializeProcedureFlowchartForNetwork(flowchart, procedureType, {
      cloneStepCidToDocumentVersionIdMap,
    }),
    duplicatingProcedureId: procedureIdToDuplicate,
  });
  return {id: createdId};
}

export async function duplicateLegacyProcedure({
  currentProcedure,
  name,
  templateCollectionId,
  procedureType,
}: {
  currentProcedure: ProcedureWithMetadata | null;
  name: string;
  templateCollectionId: string;
  procedureType: ProcedureType;
}): Promise<void> {
  logProfilerTimingEvent(PROCEDURE_DUPLICATE_STARTED_EVENT);
  if (currentProcedure == null) {
    notifications.error('Procedure was unexpectedly null, unable to duplicate');
    return;
  }
  const {newFlowchart, cloneStepCidToDocumentVersionIdMap} = getClonedFlowchart(currentProcedure.flowchart);
  const createParams = {
    name,
    procedureIdToDuplicate: currentProcedure.metadata.id,
    templateCollectionId,
    procedureType,
    flowchart: newFlowchart,
    cloneStepCidToDocumentVersionIdMap,
  };

  await withLoadingScreen(
    bufferForUX(
      createDuplicateProcedure(createParams).then(({id}) => {
        navigateToURL(procedurePageUrl(id));
        trackDuplicatedProcedureEvent(
          currentProcedure.metadata.id,
          id,
          currentProcedure.metadata.procedureType,
          templateCollectionId
        );
      })
    ),
    `Duplicating ${getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
      stringCase: StringCase.LOWER,
    })}...`
  );
  const metadata = composeProcedureEventMetadataFromFlowchart(
    true,
    currentProcedure.metadata.procedureType,
    newFlowchart,
    false
  );
  logProfilerTimingEvent(PROCEDURE_DUPLICATE_ENDED_EVENT, metadata);
}

export async function duplicateProcedure({
  sourceProcedureId,
  name,
  templateCollectionId,
  procedureType,
}: {
  sourceProcedureId: string;
  name: string;
  templateCollectionId: string;
  procedureType: ProcedureType;
}): Promise<void> {
  logProfilerTimingEvent(PROCEDURE_DUPLICATE_STARTED_EVENT);
  const cloneParams = {
    name,
    templateCollectionId,
    sourceProcedureId,
  };
  await withLoadingScreen(
    bufferForUX(
      ProcedureDAO.cloneProcedure(cloneParams).then(({cloneProcedure: {id, sizeTrackingMetadata}}) => {
        navigateToURL(procedurePageUrl(id));
        trackDuplicatedProcedureEvent(sourceProcedureId, id, procedureType, templateCollectionId);
        const metadata = composeProcedureEventMetadata(true, procedureType, sizeTrackingMetadata, true);
        logProfilerTimingEvent(PROCEDURE_DUPLICATE_ENDED_EVENT, metadata);
      })
    ),
    `Duplicating ${getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
      stringCase: StringCase.LOWER,
    })}...`
  );
}
